import {
  Box,
  Title,
  Card,
  GetColorStatus,
  capitalizeFirstLetter,
  TableProps,
  UploadBatchFileDetailseResponse,
  formattedCurrency,
  sanitizeToNumeric,
} from 'vopay';
import { FunctionComponent, useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import useFetchUploadedFileDetails from '@/hooks/useApi/useFetchUploadedFileDetails';
import styles from './Details.module.scss';
import useFileDownload from '@/hooks/useFiledDownload';
import useStorage from '@/hooks/useStorage';
import { useTranslation } from 'react-i18next';
import { UtilButton, Button, TransactionStatus, Table } from 'UI';

type FC = FunctionComponent;

type MyBatchDetails = {
  BatchName: string;
  ResponseData?: { [key: string]: string };
  Status: string;
  TransactionID: number;
  TransactionPayload: { [key: string]: string };
  BatchTransactionRequestDetailsRecordID: number;
  FailureReason: string;
};

const BulkTransactionDetails: FC = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const blacklistColumns = ['Message', 'Document', 'Id', 'Success'];
  const [fileDetails, fetchFileDetails] = useFetchUploadedFileDetails({});
  const [processedFileDetails, setProcessedFileDetails] = useState<UploadBatchFileDetailseResponse>(fileDetails);
  const storage = useStorage();
  const [autoRefresh, setAutoRefresh] = useState<number>(
    Number.parseInt(storage.autoRefresh.getAutoRefresh() || '60', 10),
  );

  const [tableColumns, setTableColumns] = useState<{ [key: string]: string }>({});
  const { downloadCsvFile } = useFileDownload();
  const { t } = useTranslation('translations', { keyPrefix: 'FileUpload.History.Details' });
  const { t: tFileHistory } = useTranslation('translations', { keyPrefix: 'FileUpload.History' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const history = useHistory();

  useEffect(() => {
    fetchFileDetails({
      BatchTransactionRequestID: Number.parseInt(batchId, 10),
    });
  }, []);
  useEffect(() => {
    setProcessedFileDetails(getDetails());
  }, [fileDetails]);
  useEffect(() => {
    setTableColumns(generateHeader());
  }, [processedFileDetails]);
  useEffect(() => {}, [autoRefresh]);
  useEffect(() => {
    let counter = 0;
    const usersTimeoutId = setInterval(() => {
      counter += 1;
      if (autoRefresh !== 0 && counter > autoRefresh) {
        counter = 0;
        fetchFileDetails({
          BatchTransactionRequestID: Number.parseInt(batchId, 10),
        });
      }
    }, 1000);
    return () => {
      clearInterval(usersTimeoutId);
    };
  }, [autoRefresh]);
  const SetAutoRefreshVal = (autoRefreshVal: number) => {
    setAutoRefresh(autoRefreshVal);
    storage.autoRefresh.storeAutoRefresh(String(autoRefreshVal));
  };

  // Function to convert keys into more readable labels
  const formatHeaderLabel = (key: string): string => {
    const headerLabels: { [key: string]: string } = {
      TransactionID: 'Transaction ID',
      ReceiverName: 'Receiver Name',
      RecipientName: 'Recipient Name',
      FirstName: 'First Name',
      LastName: 'Last Name',
      BusinessName: 'Business Name',
      CompanyName: 'Company Name',
      Address1: 'Address Line 1',
      Address2: 'Address Line 2',
      PostalCode: 'Postal Code',
      PaymentType: 'Payment Type',
      ClientAccountID: 'Client Account ID',
      NameOfFrequency: 'Name of Frequency',
      SemiMonthlyFrequencyType: 'Semi Monthly Frequency Type',
      ScheduledStartDate: 'Scheduled Start Date',
      ScheduledEndDate: 'Scheduled End Date',
      EndingAfterPayments: 'Ending After Payments',
      RecipientMessage: 'Recipient Message',
      SenderName: 'Sender Name',
      ReceiverEmailAddress: 'Receiver Email Address',
      SendEmail: 'Send Email',
      InvoiceNumber: 'Invoice Number',
      TransactionLabel: 'Transaction Label',
      IframeKey: 'Iframe Key',
      TransactionStatus: 'Transaction Status',
      AccountNumber: 'Account Number',
      BranchTransitNumber: 'Branch Transit Number',
      FinancialInstitutionNumber: 'Financial Institution Number',
      CreditCardToken: 'Credit Card Token',
      InveriteRequestGUID: 'Inverite Request GUID',
      FlinksLoginID: 'Flinks Login ID',
      FlinksAccountID: 'Flinks Account ID',
      IsActive: 'Is Active',
      ErrorMessage: 'Error Message',
      IdempotencyKey: 'Idempotency Key',
      ClientReferenceNumber: 'Client Reference Number',
    };

    // Return the formatted label if it exists, otherwise format the key by adding spaces before capitals
    return (
      headerLabels[key] ||
      key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim()
    );
  };
  const tableProps: TableProps<MyBatchDetails> = {
    id: 'fileUploadTransactions',
    version: '2',
    zebra: true,
    columns: Object.values(tableColumns).map((key) => ({
      id: key,
      header: formatHeaderLabel(key),
      align: 'left',
      template:
        key === 'Status'
          ? ({ data }) => (
              <TransactionStatus
                status={GetColorStatus(data.TransactionPayload?.Status ?? '')}
                label={capitalizeFirstLetter(data.TransactionPayload?.Status ?? '')}
              />
            )
          : key === 'TransactionID'
            ? ({ data }) =>
                data.TransactionPayload?.[key] ? (
                  <Table.Link
                    to={{
                      pathname: `/transactions-history/details/transaction/${
                        data.TransactionPayload?.TransactionID
                      }/${data.TransactionPayload?.Currency || 'CAD'}`,
                    }}
                  >
                    {data.TransactionPayload?.[key]}
                  </Table.Link>
                ) : (
                  (data.TransactionPayload?.[key] ?? '')
                )
            : key === 'Amount'
              ? ({ data }) =>
                  data.TransactionPayload.Amount
                    ? `$${formattedCurrency(sanitizeToNumeric(String(data.TransactionPayload.Amount)) || '')}`
                    : ''
              : ({ data }) => data.TransactionPayload?.[key] ?? '',
    })),
    rows: Object.values(processedFileDetails.BatchDetails).map((batch, index) => ({
      id: String(index),
      data: batch,
    })),
  };

  return (
    <Box flx="col" gap="md">
      <Box flx="col" mb="md" gapSize="16px">
        <UtilButton
          iconLeft="chevron_left"
          size="large"
          onClick={() => {
            if (history.length > 1) {
              history.goBack();
            } else {
              history.replace('/file-upload-history');
            }
          }}
        >
          {history.length > 1 ? tGlobal('Buttons.back') : tFileHistory('pageTitle')}
        </UtilButton>
        <Title title={t('pageTitle')} />
      </Box>
      <Box gapSize="12px" ai="center">
        <strong>{t('autoRefreshLabel')}</strong>{' '}
        <Button
          id="auto-refresh-15"
          size="small"
          onClick={() => {
            SetAutoRefreshVal(15);
          }}
        >
          {t('autoRefreshButton15s')}
        </Button>
        <Button
          id="auto-refresh-30"
          size="small"
          onClick={() => {
            SetAutoRefreshVal(30);
          }}
        >
          {t('autoRefreshButton30s')}
        </Button>
        <Button
          id="auto-refresh-60"
          size="small"
          onClick={() => {
            SetAutoRefreshVal(60);
          }}
        >
          {t('autoRefreshButton60s')}
        </Button>
        <Button
          id="auto-refresh-None"
          size="small"
          onClick={() => {
            SetAutoRefreshVal(0);
          }}
        >
          {t('autoRefreshButtonNone')}
        </Button>
      </Box>

      <Box>
        <Button
          id="back-export-history"
          colorScheme="primary"
          onClick={() => {
            downloadCsvFile('file-upload-overview', 'bulk-transaction');
          }}
        >
          {tGlobal('Buttons.export')}
        </Button>
      </Box>

      <Card style={{ padding: 16 }}>
        <div id="file-upload-overview" className={styles.tableContainer}>
          <Table {...tableProps} />
        </div>
      </Card>
    </Box>
  );
  function getDetails(): UploadBatchFileDetailseResponse {
    const details = fileDetails;
    const combinedDetails: UploadBatchFileDetailseResponse = { ...details };
    let TransactionPayload = {};

    Object.keys(details.BatchDetails).forEach((key) => {
      const TransactionIDKey =
        details.BatchDetails[key].ResponseData && details.BatchDetails[key].ResponseData?.PaylinkRequestID
          ? 'PaylinkRequestID'
          : 'TransactionID';

      const NewTransactionPayload = {
        [TransactionIDKey]: '',
        ErrorMessage: '',
        Status: details.BatchDetails[key].Status,
      };

      TransactionPayload = {
        ...NewTransactionPayload,
        ...details.BatchDetails[key].ResponseData,
        ...details.BatchDetails[key].TransactionPayload,
      };

      combinedDetails.BatchDetails[key] = {
        ...details.BatchDetails[key],
        BatchTransactionRequestDetailsRecordID: details.BatchDetails[key].BatchTransactionRequestDetailsRecordID,
        Status: details.BatchDetails[key].Status,

        TransactionPayload,
      };
    });

    return combinedDetails;
  }
  function generateHeader(): { [key: string]: string } {
    const details = processedFileDetails.BatchDetails;

    // Define sort priority - columns will appear in this order
    const sortPriority = [
      'ClientAccountID',
      'Account',
      'ReceiverName',
      'RecipientName',
      'TransactionID',
      'Currency',
      'Amount',

      'FirstName',
      'LastName',
      'BusinessName',
      'CompanyName',
      'Status',

      'Address1',
      'Address2',
      'City',
      'PostalCode',
      'Province',
      'Country',

      'Frequency',
      'PaymentType',
      'NameOfFrequency',
      'SemiMonthlyFrequencyType',
      'ScheduledStartDate',
      'ScheduledEndDate',
      'EndingAfterPayments',
      'RecipientMessage',
      'SenderName',
      'ReceiverEmailAddress',
      'SendEmail',
      'InvoiceNumber',
      'TransactionLabel',
      'IframeKey',

      'TransactionStatus',

      'Question',
      'Answer',
      'ABARoutingNumber',
      'FinancialInstitutionNumber',
      'BranchTransitNumber',
      'AccountNumber',
      'CreditCardToken',
      'InveriteRequestGUID',
      'FlinksLoginID',
      'FlinksAccountID',
      'Token',

      'Memo',
      'Notes',
      'IQ11',
      'IsActive',

      // To make sure these end up at the end of the list
      // 'ErrorMessage',
      // 'IdempotencyKey',
      // 'ClientReferenceNumber',
    ];

    // First, collect all available keys from the data
    const allKeys = new Set<string>();
    Object.values(details).forEach((detail) => {
      const payload = detail.TransactionPayload;
      Object.keys(payload).forEach((key) => {
        if (!blacklistColumns.includes(key)) {
          allKeys.add(key);
        }
      });
    });

    // Create header object with prioritized keys first
    const orderedHeader: { [key: string]: string } = {};

    // Add keys in priority order if they exist in the data
    sortPriority.forEach((key) => {
      if (allKeys.has(key)) {
        orderedHeader[key] = key;
        allKeys.delete(key); // Remove from remaining keys
      }
    });

    // Add any remaining keys that weren't in the priority list
    allKeys.forEach((key) => {
      orderedHeader[key] = key;
    });

    return orderedHeader;
  }
};

export default BulkTransactionDetails;
